// Chrome can fire beforeinstallprompt before React mounts. The event is kept
// at module scope so a late install gate can still use the native prompt.

const INSTALLED_KEY = "bombila.pwaInstalled";
let deferredPrompt = null;
const subscribers = new Set();
let started = false;

const notify = () => {
  for (const listener of subscribers) listener();
};

export const rememberPwaInstalled = () => {
  try {
    localStorage.setItem(INSTALLED_KEY, "1");
  } catch (error) {
    // Private mode can reject storage; installability still works in-session
  }
};

export const wasPwaInstalled = () => {
  try {
    return localStorage.getItem(INSTALLED_KEY) === "1";
  } catch (error) {
    return false;
  }
};

// Deleting the WebAPK icon does not clear site data, so the flag outlives the
// install. The gate clears it when getInstalledRelatedApps confirms the app
// is actually gone.
export const clearPwaInstalled = () => {
  try {
    localStorage.removeItem(INSTALLED_KEY);
  } catch (error) {
    // Private mode can reject storage; the in-memory state still updates
  }
};

const rememberStandaloneRun = () => {
  if (typeof window === "undefined") return;
  const standalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.matchMedia?.("(display-mode: fullscreen)")?.matches ||
    window.navigator.standalone === true;
  if (standalone) rememberPwaInstalled();
};

export const startPwaInstallCapture = () => {
  if (started || typeof window === "undefined") return;
  started = true;
  rememberStandaloneRun();
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    rememberPwaInstalled();
    deferredPrompt = null;
    notify();
  });
};

export const getDeferredPrompt = () => deferredPrompt;

export const subscribePwaInstall = (listener) => {
  startPwaInstallCapture();
  subscribers.add(listener);
  return () => subscribers.delete(listener);
};

export const promptPwaInstall = async () => {
  const prompt = deferredPrompt;
  if (!prompt) return { outcome: "unavailable" };
  deferredPrompt = null;
  notify();
  try {
    prompt.prompt();
    const choice = await prompt.userChoice;
    return { outcome: choice?.outcome ?? "dismissed" };
  } catch (error) {
    return { outcome: "error", message: error.message };
  }
};
